
import { Box, useMediaQuery, IconButton, Typography, useTheme, Tooltip as MuiTooltip } from "@mui/material";
import { PlayArrow, Pause, SkipPrevious, SkipNext } from "@mui/icons-material";
import { defaults } from "chart.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect, useState, useCallback } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatNumberWithLocale } from "../../utils/charts";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);



export default function PyramidChart({ data, showDataLabels, hideSubregions, title }) {

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const ref = useRef(null);
    
    // Time bar state
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentYear, setCurrentYear] = useState(data.latestYear);
    const [availableYears, setAvailableYears] = useState([]);
    const [speedMultiplier, setSpeedMultiplier] = useState(1);
    const intervalRef = useRef(null);
    
    const speedOptions = [0.5, 1, 2, 4]; // Professional playback speeds
    
    // Extract available years from raw data
    useEffect(() => {
        if (data.rawData && Array.isArray(data.rawData)) {
            const years = Array.from(new Set(data.rawData.map(item => parseInt(item.year)))).filter(Boolean).sort();
            setAvailableYears(years);
            if (years.length > 0 && !currentYear) {
                setCurrentYear(Math.max(...years));
            }
        } else {
            // Fallback to latestYear if rawData not available
            setAvailableYears([data.latestYear]);
        }
    }, [data.rawData, data.latestYear, currentYear]);
    
    // Auto-play functionality
    useEffect(() => {
        if (isPlaying && availableYears.length > 1) {
            intervalRef.current = setInterval(() => {
                setCurrentYear(prev => {
                    const currentIndex = availableYears.indexOf(prev);
                    const nextIndex = (currentIndex + 1) % availableYears.length;
                    return availableYears[nextIndex];
                });
            }, 1500 / speedMultiplier); // Adjust speed based on multiplier
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isPlaying, availableYears, speedMultiplier]);

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);
    
    // Transform data for current year
    const transformDataForYear = useCallback((year) => {
        if (!data.rawData || !Array.isArray(data.rawData)) {
            return data; // Fallback to original data
        }
        
        // Filter data for the selected year
        const filteredData = data.rawData.filter(item => parseInt(item.year) === year);
        
        if (filteredData.length === 0) {
            return data; // Fallback if no data for year
        }
        
        const allAgeGroups = Array.from(new Set(filteredData.map(item => item.age || item.item)));
        const sortedAgeGroups = allAgeGroups.sort((a, b) => {
            const aStart = parseInt(a.split("-")[0]) || 0;
            const bStart = parseInt(b.split("-")[0]) || 0;
            return bStart - aStart;
        });

        const datasets = [
            {
                label: 'Male',
                stack: `year-${year}`,
                backgroundColor: "#478ed5",
                data: sortedAgeGroups.map(age => {
                    const entry = filteredData.find(d => (d.age || d.item) === age && d.sex_code === "1");
                    return entry ? entry.value : 0;
                }),
            },
            {
                label: 'Female',
                stack: `year-${year}`,
                backgroundColor: "#8200e5",
                data: sortedAgeGroups.map(age => {
                    const entry = filteredData.find(d => (d.age || d.item) === age && d.sex_code === "2");
                    return entry ? -entry.value : 0;
                }),
            },
        ];

        const totalMale = datasets[0].data.reduce((sum, val) => sum + Math.abs(val), 0);
        const totalFemale = datasets[1].data.reduce((sum, val) => sum + Math.abs(val), 0);

        return {
            labels: sortedAgeGroups,
            datasets,
            totalMale,
            totalFemale,
            latestYear: year,
            info: { ...data.info, latestYear: year },
            rawData: data.rawData
        };
    }, [data]);
    
    // Control functions
    const handlePlayPause = () => {
        setIsPlaying(prev => !prev);
    };
    
    const handleYearChange = (event, newYear) => {
        setCurrentYear(newYear);
        setIsPlaying(false); // Stop playing when manually selecting
    };
    
    const handlePrevious = () => {
        const currentIndex = availableYears.indexOf(currentYear);
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : availableYears.length - 1;
        setCurrentYear(availableYears[prevIndex]);
        setIsPlaying(false);
    };
    
    const handleNext = () => {
        const currentIndex = availableYears.indexOf(currentYear);
        const nextIndex = (currentIndex + 1) % availableYears.length;
        setCurrentYear(availableYears[nextIndex]);
        setIsPlaying(false);
    };
    
    const handleSpeedChange = () => {
        const currentIndex = speedOptions.indexOf(speedMultiplier);
        const nextIndex = (currentIndex + 1) % speedOptions.length;
        setSpeedMultiplier(speedOptions[nextIndex]);
    };
    
    // Get data for current year
    const currentData = transformDataForYear(currentYear);


    const chartData = {
        labels: currentData.labels,
        datasets: [
            ...currentData.datasets.map((dataset, index) => ({
                ...dataset,
                barThickness: 9,
            })),
        ].filter(Boolean),
    };


    const options = {
        indexAxis: "y",
        layout: {
            padding: {
                top: 5,
                right: isMobile ? 5 : 10,
                bottom: 12,
                left: isMobile ? 5 : 10,
            },
        },
        interaction: {
            mode: "nearest",
            axis: "xy",
            intersect: false,
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    color: theme.palette.primary.fontColor,
                    callback: (value) => formatNumberWithLocale(Math.abs(value)),
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: theme.palette.mode === "dark" ? "#333" : "#e7e7e7",
                },

                title: {
                    display: false,
                    text: data.info.unit,
                    color: theme.palette.primary.fontColor,
                    padding: 0,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                    padding: 10,
                    callback: function(value, index, ticks) {
                        // Get the actual label from the chart data instead of formatting the index
                        const chart = this.chart;
                        return chart.data.labels[index] || value;
                    },
                },
            },
        },
        animation: {
            duration: 600, // Smooth transition duration
            easing: 'easeInOutQuart', // Smooth easing function
        },
        transitions: {
            active: {
                animation: {
                    duration: 400
                }
            }
        },
        plugins: {
            legend: {
                position: "top",
                labels: {
                    padding: isMobile ? 8 : 11,
                    usePointStyle: true,
                    color: theme.palette.primary.fontColor,
                    generateLabels: function (chart) {
                        const original = defaults.plugins.legend.labels.generateLabels;
                        const labelsOriginal = original.call(this, chart);
                        const { totalMale, totalFemale } = currentData;

                        return labelsOriginal.map((label) => {
                            let valueText = "";

                            if (label.text.toLowerCase() === "male") {
                                valueText = `: ${formatNumberWithLocale(totalMale)}`;
                            } else {
                                valueText = `: ${formatNumberWithLocale(totalFemale)}`;
                            }

                            return {
                                ...label,
                                text: label.text + valueText,
                            };
                        });
                    },
                },
            },
            datalabels: {
                color: theme.palette.primary.fontColor,
                align: (context) =>
                    context.dataset.data[context.dataIndex] < 0 ? "start" : "end",
                anchor: (context) =>
                    context.dataset.data[context.dataIndex] < 0 ? "start" : "end",
                clamp: true,
                display: showDataLabels ? "auto" : false,
                backgroundColor: "rgba(255, 255, 255, 0)",
                font: {
                    size: 10,
                },
                padding: 4,
                formatter: (value) => formatNumberWithLocale(Math.abs(value)),
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.dataset.label || "";
                        const value = formatNumberWithLocale(Math.abs(
                            context.parsed?.x ?? context.parsed
                        ));
                        const unit = currentData.info.unit || "";
                        return `${label}: ${value} ${unit}`;
                    },
                    labelTextColor: function () {
                        return "#000";
                    },
                    footer: function (context) {
                        const flagDescription = "Median";
                        return flagDescription ? `${flagDescription}` : "";
                    },
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                footerColor: "#333",
                footerFont: { weight: "normal" },
                borderColor: "#ccc",
                borderWidth: "1",
            },
            title: {
                display: true,
                text: `Median population in the year ${currentYear}`,
                color: "#ed9812",
                font: {
                    size: 13,
                    lineHeight: 1,
                },
                padding: { top: 0, left: 0, right: 0, bottom: 5 },
            },
        },
    };

    // No height on the wrapper below: it also holds the timeline, and pinning its height is
    // what made the canvas and its container derive their size from each other. The height
    // belongs to the chart box further down.
    return (
        <Box
            sx={{
                '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.4 },
                    '100%': { opacity: 1 }
                },
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            {availableYears.length > 1 && (
                <Box 
                    sx={{ 
                        mb: { xs: 1, md: 1.5 }, 
                        ml: isMobile ? '5px' : '10px',
                        mr: isMobile ? '5px' : '10px',
                        p: { xs: 0.75, md: 1 },
                        background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.background.paper} 100%)`,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}
                >
                    {/* Header with current year and controls */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: { xs: 0.5, md: 0.75 },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        gap: { xs: 0.25, sm: 0 }
                    }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 600,
                                color: theme.palette.primary.main,
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                                order: { xs: 1, sm: 1 }
                            }}
                        >
                            {isMobile ? currentYear : `Timeline: ${currentYear}`}
                        </Typography>
                        
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: { xs: 0.125, md: 0.25 },
                            order: { xs: 2, sm: 2 }
                        }}>
                            <IconButton 
                                onClick={handlePrevious} 
                                size="small"
                                sx={{ 
                                    color: theme.palette.primary.main,
                                    minWidth: { xs: 30, md: 32 },
                                    minHeight: { xs: 30, md: 32 },
                                    p: { xs: 0.25, md: 0.5 },
                                    '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                                }}
                            >
                                <SkipPrevious sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                            
                            <IconButton 
                                onClick={handlePlayPause} 
                                size="small"
                                sx={{ 
                                    color: 'white',
                                    backgroundColor: isPlaying ? theme.palette.error.main : theme.palette.primary.main,
                                    mx: { xs: 0.125, md: 0.25 },
                                    minWidth: { xs: 32, md: 34 },
                                    minHeight: { xs: 32, md: 34 },
                                    p: { xs: 0.5, md: 0.625 },
                                    '&:hover': { 
                                        backgroundColor: isPlaying ? theme.palette.error.dark : theme.palette.primary.dark,
                                        transform: 'scale(1.05)'
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isPlaying ? <Pause sx={{ fontSize: { xs: 16, md: 18 } }} /> : <PlayArrow sx={{ fontSize: { xs: 16, md: 18 } }} />}
                            </IconButton>
                            
                            <IconButton 
                                onClick={handleNext} 
                                size="small"
                                sx={{ 
                                    color: theme.palette.primary.main,
                                    minWidth: { xs: 30, md: 32 },
                                    minHeight: { xs: 30, md: 32 },
                                    p: { xs: 0.25, md: 0.5 },
                                    '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                                }}
                            >
                                <SkipNext sx={{ fontSize: { xs: 16, md: 18 } }} />
                            </IconButton>
                            
                            <MuiTooltip title="Speed" arrow placement="top">
                                <Box 
                                    onClick={handleSpeedChange}
                                    sx={{
                                        ml: { xs: 0.25, md: 0.5 },
                                        px: { xs: 0.5, md: 0.625 },
                                        py: { xs: 0.125, md: 0.25 },
                                        borderRadius: 0.5,
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        backgroundColor: theme.palette.background.paper,
                                        cursor: 'pointer',
                                        minWidth: { xs: 28, md: 30 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: theme.palette.primary.main + '10',
                                            transform: 'scale(1.05)'
                                        }
                                    }}
                                >
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                            fontSize: { xs: '0.6rem', md: '0.65rem' },
                                            lineHeight: 1,
                                            userSelect: 'none'
                                        }}
                                    >
                                        {speedMultiplier}×
                                    </Typography>
                                </Box>
                            </MuiTooltip>
                        </Box>
                    </Box>
                    
                    {/* Visual Timeline Bar */}
                    <Box sx={{ position: 'relative', mb: { xs: 0.5, md: 1 } }}>
                        {/* Background timeline */}
                        <Box 
                            sx={{ 
                                height: { xs: 6, md: 8 },
                                backgroundColor: theme.palette.divider,
                                borderRadius: 4,
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Progress bar showing current position */}
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${((currentYear - Math.min(...availableYears)) / (Math.max(...availableYears) - Math.min(...availableYears))) * 100}%`,
                                    backgroundColor: theme.palette.primary.main,
                                    borderRadius: 4,
                                    transition: 'width 0.3s ease',
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
                                }}
                            />
                        </Box>
                        
                        {/* Year markers */}
                        <Box sx={{ 
                            position: 'absolute', 
                            top: { xs: -2.5, md: -2 }, 
                            left: 0, 
                            right: 0, 
                            height: { xs: 10, md: 10 },
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                            {availableYears.map((year, index) => {
                                const isActive = year === currentYear;
                                return (
                                    <Box
                                        key={year}
                                        onClick={() => handleYearChange(null, year)}
                                        sx={{
                                            width: { xs: 10, md: 10 },
                                            height: { xs: 10, md: 10 },
                                            borderRadius: '50%',
                                            backgroundColor: isActive ? theme.palette.primary.main : theme.palette.action.disabled,
                                            border: `2px solid ${isActive ? 'white' : theme.palette.background.paper}`,
                                            cursor: 'pointer',
                                            transform: isActive ? 'scale(1.2)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                            boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
                                            // Better click target
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: { xs: 28, md: 24 },
                                                height: { xs: 28, md: 24 },
                                                borderRadius: '50%',
                                                // Invisible clickable area
                                            },
                                            '&:hover': {
                                                transform: 'scale(1.15)',
                                                backgroundColor: theme.palette.primary.main
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                    
                    {(availableYears.length <= (isMobile ? 4 : 6)) && (
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            mt: { xs: 0.5, md: 0.75 },
                            px: 0.25
                        }}>
                            {availableYears.filter((_, index) => {
                                const divisor = isMobile ? Math.max(2, Math.floor(availableYears.length / 3)) : Math.max(1, Math.floor(availableYears.length / 8));
                                return index % divisor === 0;
                            }).map((year) => (
                                <Typography 
                                    key={year}
                                    variant="caption" 
                                    sx={{ 
                                        color: year === currentYear ? theme.palette.primary.main : theme.palette.text.secondary,
                                        fontWeight: year === currentYear ? 600 : 400,
                                        fontSize: { xs: '0.6rem', md: '0.65rem' }
                                    }}
                                >
                                    {year}
                                </Typography>
                            ))}
                            {availableYears.length > (isMobile ? 3 : 6) && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: Math.max(...availableYears) === currentYear ? theme.palette.primary.main : theme.palette.text.secondary,
                                        fontWeight: Math.max(...availableYears) === currentYear ? 600 : 400,
                                        fontSize: { xs: '0.6rem', md: '0.65rem' }
                                    }}
                                >
                                    {Math.max(...availableYears)}
                                </Typography>
                                )}
                        </Box>
                    )}
                </Box>
            )}
            {/* Same shape as every other chart in the app: the canvas hangs directly off a box
                whose height is set in vh by .max-chart-height. Chart.js measures its parent to
                size the canvas, so that parent must have a height of its own - here it used to
                be a flex item with height:auto, which left the two resolving against each other
                and the chart overflowing the card. */}
            <Box className="max-chart-height">
                <Bar options={options} data={chartData} ref={ref} />
            </Box>
        </Box>
    );
}